import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../lib/api-client';

export type ProductDetail = {
  sku: string;
  designation: string;
  famille: string;
  sous_famille: string | null;
  marque: string | null;
  dn: number | null;
  pn: number | null;
  materiau: string | null;
  raccordement: string | null;
  normes: string[];
  prix_unitaire_ht: number;
  devise: string;
  unite: string;
  stock: number | null;
  fiche_technique: string | null;
  description_longue: string | null;
  characteristics?: Record<string, unknown> | null;
  actif: boolean;
  created_at: number;
  updated_at: number;
};

export function ProductDetailModal({
  sku,
  product,
  onClose,
}: {
  sku?: string | null;
  product?: ProductDetail | null;
  onClose: () => void;
}) {
  const [loaded, setLoaded] = useState<ProductDetail | null>(product ?? null);
  const [error, setError] = useState<string | null>(null);
  const targetSku = product?.sku ?? sku ?? null;

  useEffect(() => {
    if (product) {
      setLoaded(product);
      return;
    }
    if (!targetSku) return;
    setLoaded(null);
    setError(null);
    api
      .get<ProductDetail>(`/api/catalogues/products/${encodeURIComponent(targetSku)}`)
      .then(setLoaded)
      .catch((err) => setError(err instanceof Error ? err.message : 'Produit introuvable'));
  }, [product, targetSku]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-ink-950/80 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="tc-panel w-full max-w-3xl mt-8">
        <div className="px-5 py-4 border-b border-ink-700 flex items-start justify-between gap-4">
          <div>
            <div className="tc-label mb-1">catalogue / fiche produit</div>
            <h2 className="text-lg font-display tracking-tight">
              {targetSku ? <span className="font-mono text-amber-500">{targetSku}</span> : 'Produit'}
            </h2>
            {loaded?.designation && (
              <p className="text-sm text-steel-300 mt-1 max-w-2xl">{loaded.designation}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="text-steel-500 hover:text-steel-100 inline-flex h-8 w-8 items-center justify-center rounded"
          >
            <X aria-hidden className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>

        <div className="p-5">
          {error && (
            <div className="border border-red-900/60 bg-red-900/20 text-red-300 px-4 py-3 text-sm font-mono">
              ERROR · {error}
            </div>
          )}
          {!error && !loaded && (
            <div className="text-sm text-steel-500 font-mono">Chargement du produit...</div>
          )}
          {loaded && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink-700 border border-ink-700">
                <InfoCell label="famille" value={loaded.famille} />
                <InfoCell label="sous-famille" value={loaded.sous_famille} />
                <InfoCell label="marque" value={loaded.marque} />
                <InfoCell label="statut" value={loaded.actif ? 'actif' : 'inactif'} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink-700 border border-ink-700">
                <InfoCell label="DN" value={loaded.dn} />
                <InfoCell label="PN" value={loaded.pn} />
                <InfoCell label="materiau" value={loaded.materiau} />
                <InfoCell label="raccordement" value={loaded.raccordement} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink-700 border border-ink-700">
                <InfoCell
                  label="prix HT"
                  value={`${loaded.prix_unitaire_ht.toLocaleString('fr-FR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} ${loaded.devise}`}
                />
                <InfoCell label="unite" value={loaded.unite} />
                <InfoCell label="stock" value={loaded.stock} />
                <InfoCell label="fiche technique" value={loaded.fiche_technique} />
              </div>

              <section>
                <div className="tc-label mb-2">normes</div>
                {loaded.normes.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {loaded.normes.map((n) => (
                      <span key={n} className="text-2xs font-mono border border-ink-700 bg-ink-900 px-2 py-1 text-steel-300">
                        {n}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-steel-700">Aucune norme renseignee</div>
                )}
              </section>

              <section>
                <div className="tc-label mb-2">caracteristiques catalogue</div>
                {configurationRows(loaded.characteristics).length > 0 && (
                  <div className="mb-4 overflow-x-auto border border-ink-700">
                    <table className="min-w-full">
                      <thead>
                        <tr>
                          <th>FEUILLE</th>
                          <th>PARAMETRE</th>
                          <th>VALEUR</th>
                          <th>VARIANTE</th>
                          <th>FAMILLES</th>
                        </tr>
                      </thead>
                      <tbody>
                        {configurationRows(loaded.characteristics).map((row, idx) => (
                          <tr key={idx}>
                            <td className="font-mono text-2xs text-steel-300">{formatValue(row.source_sheet)}</td>
                            <td className="font-mono text-2xs text-steel-300">{formatValue(row.param_name)}</td>
                            <td className="font-mono text-2xs text-steel-100">{formatValue(row.param_value ?? row.param_threshold)}</td>
                            <td className="font-mono text-2xs text-amber-500">{formatValue(row.variant)}</td>
                            <td className="text-2xs text-steel-400">{formatValue(row.families)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {characteristicEntries(loaded.characteristics).length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-700 border border-ink-700">
                    {characteristicEntries(loaded.characteristics).map(([key, value]) => (
                      <InfoCell key={key} label={formatKey(key)} value={formatValue(value)} />
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-steel-700">Aucune caracteristique detaillee renseignee</div>
                )}
              </section>

              {loaded.description_longue && (
                <section>
                  <div className="tc-label mb-2">description longue</div>
                  <div className="text-sm text-steel-300 whitespace-pre-wrap leading-relaxed">
                    {loaded.description_longue}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function characteristicEntries(value: ProductDetail['characteristics']): Array<[string, unknown]> {
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value).filter(([, v]) => {
    if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object') return false;
    if (v === null || v === undefined || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    return true;
  });
}

function configurationRows(value: ProductDetail['characteristics']): Array<Record<string, unknown>> {
  const configs = value?.configurations;
  if (!Array.isArray(configs)) return [];
  return configs.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item));
}

function formatKey(key: string): string {
  return key.replace(/_/g, ' ');
}

function formatValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(formatValue).join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value);
  return value === null || value === undefined || value === '' ? '—' : String(value);
}

function InfoCell({ label, value }: { label: string; value: unknown }) {
  const display = value === null || value === undefined || value === '' ? '—' : String(value);
  return (
    <div className="bg-ink-950 px-3 py-2 min-h-[4rem]">
      <div className="tc-label mb-1">{label}</div>
      <div className="text-sm text-steel-100 break-words">{display}</div>
    </div>
  );
}
